import {IRequestAuth} from 'core/api'
import {
  isValidationErrorResponse,
  IValidationErrorResponseBody,
} from 'core/api/client'
import {createPatientPrescription} from 'core/api/patient'
import {
  createSubject,
  deletePrescription,
  reactivatePrescription,
  getSubjects,
  ICreatePrescriptionResponse,
  ICreateSubjectResponse,
  IDeleteSubjectPrescriptionResponse,
  IGetSubjectsResponse,
  IGetSubjectsSuccessResponse,
  IErrorResponse
} from 'core/api/study_coordinator'
import {buildPrescriptionFromTemplateKey, PrescriptionTemplatesByKey} from 'core/prescription'
import {startSubmit, stopSubmit} from 'redux-form'
import {delay} from 'redux-saga'
import {all, call, fork, put, race, select, take, takeLatest} from 'redux-saga/effects'
import * as Features from 'study_coordinator/features'
import * as routes from 'study_coordinator/routes'
import {actions as mainActions} from '../main'
import {
  clearSubjects,
  getSubjectsRequest as getSubjectsRequestAction,
  IActionAddSubjectRequest,
  IActionCreateNewPrescriptionRequest,
  IActionCreatePrescriptionRequest,
  IActionDeactivateSubjectRequest,
  IActionReactivateSubjectRequest,
  IActionGetSubjectsRequest,
  IActionUpdatePrescriptionRequest,
  resetIsFetchingSubjects,
  resetIsSubjectActivationConfirmActive,
  setIsFetchingSubjects,
  setSubjects,
} from './actions'
import * as actions from './actionTypes'
import {ISelectSubject} from './model'
import {selectSubject} from './selectors'
import {
  IDependencies as TreatmentReportSagaDependencies,
  saga as treatmentReportSaga,
} from './treatmentReport'

const DEFAULT_ERROR_MESSAGE = 'Oops! Something went wrong. Please try again later.'
const DEFAULT_NETWORK_ERROR_MESSAGE = 'Network error, please check your connection and try again.'

/**
 * Dependencies needed for the sagas
 */
export interface IDependencies extends IMainSagaDependencies {
  subjectIdentifierToPatientId: (subjectIdentifier: string) => void
  subjectIdentifierToParticipantId: (subjectIdentifier: string) => void
}

/**
 * Dependencies that need to be injected into the main saga
 */
export interface IMainSagaDependencies {
  withAuth: (fn: (...args: any[]) => any) => void
  withTokenAuth: (fn: (auth: IRequestAuth, ...args: any[]) => any) => void
  onTokenExpired: () => void
  redirectTo: (path: string) => void
}

export function* addSubjectRequest(dependencies: IDependencies, action: IActionAddSubjectRequest) {
  yield put(startSubmit(action.form))
  let errors = {}

  let response: ICreateSubjectResponse
  try {
    const query = Features.generateSubjectIdentifiers() ? 'skipSubjectIdentifierWhitelistValidation=true' : undefined
    response = yield call(dependencies.withAuth, createSubject, action.payload, query)
  } catch (e) {
    // TODO abstract network error handling to be easier to generalize across all API reqs
    // TODO I18N
    const error = 'Network error'
    yield put(stopSubmit(action.form, {...errors, _error: error}))
    return
  }

  let formError: string | undefined = undefined
  if (response.status === 200) {
    yield call(dependencies.redirectTo, routes.subjectConfirmRoute(action.payload.subjectIdentifier))
    yield put(mainActions.snackbarSetRequest(`Successfully created Token ${response.data.accessCode}`))
  } else if (isValidationErrorResponse(response.data)) {
    const body: IValidationErrorResponseBody = response.data
    errors = body.validation.errors.reduce((memo, err) => ({
      ...memo,
      [err.key]: err.message,
    }), {})
    // TODO I18N
    formError = 'Invalid data'
  } else if (response.status === 400) {
    // TODO I18N
    formError = 'Invalid data'
  } else if (response.status === 401) {
    yield call(dependencies.onTokenExpired)
  } else if (response.status === 409) {
    // TODO I18N
    formError = 'Subject with id already exists'
  } else if (response.status === 500) {
    // TODO I18N
    formError = 'Server-side error'
  } else {
    // TODO I18N
    formError = 'Unknown error'
  }

  yield put(stopSubmit(action.form, {...errors, _error: formError}))
}

const GET_SUBJECTS_REQUEST_COMPLETE = {
  type: 'subjects/GET_SUBJECTS_REQUEST_COMPLETE',
}
/**
 * Saga for calling `getSubjectsRequest` and waiting on completion (or timeout).
 *
 * Usage: `call(getSubjectsRequestWait)`
 */
export function* getSubjectsRequestWait() {
  yield put(getSubjectsRequestAction())
  yield race({
    isComplete: take(GET_SUBJECTS_REQUEST_COMPLETE.type),
    isTimeout: call(delay, 30000), // timeout 30 seconds
  })
}

export function* getSubjectsRequest(dependencies: IDependencies, action: IActionGetSubjectsRequest) {
  yield put(setIsFetchingSubjects())
  let response: IGetSubjectsResponse
  try {
    response = yield call(dependencies.withAuth, getSubjects)
  } catch (e) {
    // TODO I18N
    yield put(mainActions.snackbarSetRequest(DEFAULT_NETWORK_ERROR_MESSAGE))
    yield call(dependencies.redirectTo, routes.rootRoute())
    yield put(resetIsFetchingSubjects())
    yield put(GET_SUBJECTS_REQUEST_COMPLETE)
    return
  }
  if (response.status === 200) {
    yield put(clearSubjects())
    const data = response.data as IGetSubjectsSuccessResponse
    const subjects = Object.keys(data.participants).map((participantId) => {
      const participant = data.participants[participantId]
      return {
        id: participantId,
        subjectIdentifier: participant.subjectIdentifier,
        age: participant.age,
        isSimpleMode: participant.isSimpleMode,
        accessCode: participant.accessCode,
        prescriptions: participant.prescriptions,
        isTreatmentActive: participant.isTreatmentActive,
        adherenceDosage: participant.adherenceDosage,
        adherenceCompletedDosage: participant.adherenceCompletedDosage,
        isTreatmentAdherent: participant.isTreatmentAdherent,
        deviceType: participant.deviceType,
        patient: participant.patient,
        partnerOrderId: participant.prescriptions.length > 0 ? participant.prescriptions[0].partnerOrderId : 'N/A',
        prescribingNpi: participant.prescriptions.length > 0 ? participant.prescriptions[0].prescribingNpi : 'N/A',
      }
    })

    yield put(setSubjects(subjects))
  } else if (response.status === 401) {
    yield call(dependencies.onTokenExpired)
  } else {
    const message = response && response.data && (response.data as IErrorResponse).message || DEFAULT_ERROR_MESSAGE
    yield put(mainActions.snackbarSetRequest(message))
  }
  yield put(resetIsFetchingSubjects())
  yield put(GET_SUBJECTS_REQUEST_COMPLETE)
}

export function* deactivateSubjectRequest(dependencies: IDependencies, action: IActionDeactivateSubjectRequest) {
  let response: IDeleteSubjectPrescriptionResponse
  try {
    const participantId = yield call(
      dependencies.subjectIdentifierToParticipantId,
      action.payload.subjectIdentifier,
    )
    const payload = {
      activationNotes: action.payload.reason
    }
    response = yield call(dependencies.withAuth, deletePrescription, participantId, payload)
  } catch (e) {
    // TODO I18N
    yield put(mainActions.snackbarSetRequest(DEFAULT_NETWORK_ERROR_MESSAGE))
    yield put(resetIsSubjectActivationConfirmActive())
    return
  }

  if (response.status === 200) {
    yield call(getSubjectsRequestWait)
    yield put(mainActions.snackbarSetRequest('Subject treatment successfully deactivated!'))
  } else if (response.status === 401) {
    yield call(dependencies.onTokenExpired)
  } else {
    // TODO I18N
    const message =
      response && response.data && (response.data as IErrorResponse).message || DEFAULT_ERROR_MESSAGE
    yield put(mainActions.snackbarSetRequest(message))
  }

  yield put(resetIsSubjectActivationConfirmActive())
}

export function* reactivateSubjectRequest(dependencies: IDependencies, action: IActionReactivateSubjectRequest) {
  let response: IDeleteSubjectPrescriptionResponse
  try {
    const participantId = yield call(
      dependencies.subjectIdentifierToParticipantId,
      action.payload.subjectIdentifier,
    )
    response = yield call(dependencies.withAuth, reactivatePrescription, participantId)
  } catch (e) {
    // TODO I18N
    yield put(mainActions.snackbarSetRequest(DEFAULT_NETWORK_ERROR_MESSAGE))
    yield put(resetIsSubjectActivationConfirmActive())
    return
  }
  if (response.status === 200) {
    yield call(getSubjectsRequestWait)
    yield put(mainActions.snackbarSetRequest('Subject treatment successfully reactivated!'))
  } else if (response.status === 401) {
    yield call(dependencies.onTokenExpired)
  } else {
    // TODO I18N
    const message =
      response && response.data && (response.data as IErrorResponse).message || DEFAULT_ERROR_MESSAGE
    yield put(mainActions.snackbarSetRequest(message))
  }
  yield put(resetIsSubjectActivationConfirmActive())
}

const createPatientPrescriptionRequestFactory = (
  onSuccess: (dependencies: IDependencies, action: IActionCreatePrescriptionRequest<any>) => void,
) => (
  function* patientPrescriptionRequest(
    dependencies: IDependencies,
    action: IActionCreatePrescriptionRequest<any>,
  ) {
    yield put(startSubmit(action.form))

    const payload = buildPrescriptionFromTemplateKey(action.payload.prescriptionTemplateKey, {
      amblyopicEye: action.payload.amblyopicEye,
    })
    if (!payload) {
      yield put(stopSubmit(action.form, {_error: 'Invalid prescription template.'}))
    }

    const patientId = yield call(
      dependencies.subjectIdentifierToPatientId,
      action.payload.subjectIdentifier,
    )

    let response: ICreatePrescriptionResponse
    let errors = {}

    try {
      response = yield call(dependencies.withTokenAuth, createPatientPrescription,
        patientId,
        payload,
      )
    } catch (e) {
      yield put(mainActions.snackbarSetRequest(DEFAULT_NETWORK_ERROR_MESSAGE))
      yield put(stopSubmit(action.form, errors))
      return
    }

    if (response.status === 201) {
      yield call(onSuccess, dependencies, action)
      const template = PrescriptionTemplatesByKey[action.payload.prescriptionTemplateKey]
      const prescriptionDisplay = template ? `"${template.name}" ` : ''
      yield put(mainActions.snackbarSetRequest(`Successfully created prescription ${prescriptionDisplay}` +
        `for subject ${action.payload.subjectIdentifier}!`))
      yield put(stopSubmit(action.form))
    } else if (response.status === 401) {
      yield call(dependencies.onTokenExpired)
    } else if (response.status === 404) {
      yield put(stopSubmit(action.form, {_error: 'The subject does not exist.'}))
    } else if (isValidationErrorResponse(response.data)) {
      const body: IValidationErrorResponseBody = response.data
      errors = body.validation.errors.reduce((memo, err) => ({
        ...memo,
        [err.key]: err.message,
      }), {})
      yield put(stopSubmit(action.form, {...errors, _error: 'Invalid data.'}))
    } else if (response.status === 400) {
      yield put(stopSubmit(action.form, {_error: 'Invalid data.'}))
    } else {
      yield put(stopSubmit(action.form, {_error: DEFAULT_ERROR_MESSAGE}))
    }
  }
)

export const createNewPatientPrescriptionRequest = createPatientPrescriptionRequestFactory(
  function* onCreatePatientPrescriptionSuccess(
    dependencies: IDependencies,
    action: IActionCreateNewPrescriptionRequest,
  ) {
    yield call(dependencies.redirectTo, routes.subjectConfirmRoute(action.payload.subjectIdentifier))
  },
)

export const updatePatientPrescriptionRequest = createPatientPrescriptionRequestFactory(
  function* onCreatePatientPrescriptionSuccess(
    dependencies: IDependencies,
    action: IActionUpdatePrescriptionRequest,
  ) {
    yield call(dependencies.redirectTo, routes.subjectShowRoute(action.payload.subjectIdentifier))
  },
)

/**
 * Return the corresponding participantId for the given subjectIdentifier by looking up the subject in state.
 * If the subject can't be found in state, retrieves subjects from the API and attempts to look up again.
 * @returns string | null
 */
export function* subjectIdentifierToParticipantId(subjectIdentifier: string) {
  let subject: ISelectSubject | null = yield select(selectSubject, subjectIdentifier)
  if (!subject) {
    yield call(getSubjectsRequestWait)
    subject = yield select(selectSubject, subjectIdentifier)
  }
  return subject && subject.id
}

/**
 * Return the corresponding patientId for the given subjectIdentifier by looking up the subject in state.
 * If the subject can't be found in state, retrieves subjects from the API and attempts to look up again.
 * @returns string | null
 */
export function* subjectIdentifierToPatientId(subjectIdentifier: string) {
  let subject: ISelectSubject | null = yield select(selectSubject, subjectIdentifier)
  if (!subject) {
    yield call(getSubjectsRequestWait)
    subject = yield select(selectSubject, subjectIdentifier)
  }
  return subject && subject.patient.id
}

function* mainSaga(dependencies: IMainSagaDependencies) {
  const sagaDependencies = {
    ...dependencies,
    subjectIdentifierToPatientId,
    subjectIdentifierToParticipantId,
  }
  yield all([
    takeLatest<IActionAddSubjectRequest, IDependencies>(
      actions.ADD_SUBJECT_REQUEST,
      addSubjectRequest,
      sagaDependencies,
    ),
    takeLatest<IActionGetSubjectsRequest, IDependencies>(
      actions.GET_SUBJECTS_REQUEST,
      getSubjectsRequest,
      sagaDependencies,
    ),
    takeLatest<IActionDeactivateSubjectRequest, IDependencies>(
      actions.DEACTIVATE_SUBJECT_REQUEST,
      deactivateSubjectRequest,
      sagaDependencies,
    ),
    takeLatest<IActionReactivateSubjectRequest, IDependencies>(
      actions.REACTIVATE_SUBJECT_REQUEST,
      reactivateSubjectRequest,
      sagaDependencies,
    ),
    takeLatest<IActionCreateNewPrescriptionRequest, IDependencies>(
      actions.CREATE_PRESCRIPTION_REQUEST,
      createNewPatientPrescriptionRequest,
      sagaDependencies,
    ),
    takeLatest<IActionUpdatePrescriptionRequest, IDependencies>(
      actions.UPDATE_PRESCRIPTION_REQUEST,
      updatePatientPrescriptionRequest,
      sagaDependencies,
    ),
  ])
}

function* saga(dependencies: IMainSagaDependencies) {
  yield all([
    fork<IMainSagaDependencies>(mainSaga, dependencies),
    fork<TreatmentReportSagaDependencies>(treatmentReportSaga, {
      withAuth: dependencies.withAuth,
      onTokenExpired: dependencies.onTokenExpired,
      subjectIdentifierToPatientId,
    }),
  ])
}

export default saga



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./study_coordinator/src/store/subjects/saga.ts